/** PWA install affordance helper.
 *
 *  Three kinds of state:
 *    - androidPrompt: the captured `beforeinstallprompt` event from
 *      Android Chrome / Edge / Brave. Tapping our button calls
 *      .prompt() which surfaces Chrome's native install dialog.
 *    - iosInstructable: navigator says iOS Safari + not standalone, so
 *      we show a small instruction card with the Share -> Add to
 *      Home Screen steps. iOS does not fire beforeinstallprompt.
 *    - alreadyInstalled: matchMedia '(display-mode: standalone)' fires
 *      when the user is running the installed PWA - hide the button.
 *
 *  The user can dismiss the FAB; the dismissal lives in localStorage
 *  under bv2:install-dismissed (with the timestamp). A 14-day TTL
 *  re-surfaces it so the user gets reminded if they put it off. */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let captured: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let installedFlag = false;

const DISMISS_KEY = "bv2:install-dismissed";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    captured = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    installedFlag = true;
    captured = null;
    try {
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* ignore */
    }
    notify();
  });
}

export function hasNativePrompt(): boolean {
  return !!captured;
}

export async function triggerNativePrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!captured) return "unavailable";
  const ev = captured;
  captured = null;
  notify();
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    return choice.outcome;
  } catch {
    return "unavailable";
  }
}

export function isStandalone(): boolean {
  if (installedFlag) return true;
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS legacy
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (!isIos) return false;
  // Block in-app webviews (FB / Instagram) where Add-to-Home doesn't work
  if (/FBAN|FBAV|Instagram|Line|Twitter|Snapchat/.test(ua)) return false;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function isInstallDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  notify();
}

export function clearDismiss(): void {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
